import React, { CSSProperties, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconS24CrossCircle24Filled } from '@is24/cosma-ui-icons'
import { INVALID, VALID } from '../../../../services/domain/dropdownSelect'
import { ContentStateProperty, DropdownOption, DropdownValue, LocalizedDropdownOptions } from '../../../../@types/types'
import { InputLabel } from '../../display/labelText/LabelText'
import { useFormState } from '../../../pages/insertionPage/wizard/useFormState'
import { useIsRequiredProperty } from '../../../../services/validation/requiredFieldsUtils/useIsRequiredProperty'
import { buildDropdownOptions, getNewStyles } from '../dropdowns/commonUtils'

export type DropdownSelectProps = {
  id: string
  name: ContentStateProperty
  label?: string
  labelClassName?: string
  ariaLabel?: string
  disabled?: boolean
  value?: DropdownValue
  noInformation?: string
  style?: React.CSSProperties
  options: DropdownOption[] | LocalizedDropdownOptions
  placeholder?: string
  allowEmpty?: boolean
  errors?: string[]
  onChange: (value: any) => void
  onBlur?: () => void
  onBlurTracking?: (value: any, isUnchanged: boolean) => void
  onFocus?: () => void
  className?: string
}

const DropdownSelect = ({
  id,
  label,
  labelClassName,
  name,
  ariaLabel,
  disabled,
  value,
  noInformation,
  style,
  options,
  placeholder,
  allowEmpty,
  errors = [],
  onChange,
  onBlur,
  onBlurTracking,
  onFocus,
  className,
}: DropdownSelectProps) => {
  const { t } = useTranslation()
  const { setTouched, formState } = useFormState()
  const isFieldMandatory = useIsRequiredProperty(name)

  const dropdownOptions: DropdownOption[] = useMemo(
    () => buildDropdownOptions(isFieldMandatory, options, t, noInformation),
    [isFieldMandatory, noInformation, options, t]
  )

  const returnValueOfSelectedOption = (newValue: any): DropdownOption =>
    dropdownOptions.find((option) => JSON.stringify(option.value) === newValue)!

  const getSelected = (newValue: any) =>
    !newValue || newValue === VALID || newValue === INVALID
      ? { value: null, label: null }
      : returnValueOfSelectedOption(newValue)

  const [highlightValidation, setHighlightValidation] = useState(false)
  const [isUnchanged, setIsUnchanged] = useState(true)
  const [lastValue, setLastValue] = useState(getSelected(value))
  const [actualValue, setActualValue] = useState(getSelected(value))

  const hasErrors = () => errors.length > 0 && (formState.submitCount > 0 || formState.touched[name])
  const isInvalid = (!value && highlightValidation && !allowEmpty) || hasErrors()
  const newStyles: CSSProperties = getNewStyles(style, highlightValidation, value ? [value] : undefined, isInvalid)

  useEffect(() => {
    value && setHighlightValidation(true)
    setActualValue(getSelected(JSON.stringify(value)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const onSelectChange = ({ currentTarget: { value: newValue } }: React.ChangeEvent<HTMLSelectElement>) => {
    setHighlightValidation(true)
    const selected = getSelected(newValue)
    onChange(selected)
    setActualValue(selected)
    setTouched(name)
  }

  const onLostFocus = ({ relatedTarget }: React.FocusEvent<HTMLSelectElement>) => {
    if (relatedTarget?.id !== `${id}-select-container`) {
      onBlur && onBlur()
    }

    if (lastValue?.value !== actualValue?.value) {
      onBlurTracking && onBlurTracking(actualValue, isUnchanged)
      isUnchanged && setIsUnchanged(false)
      setLastValue(actualValue)
    }
  }

  const classNames = className ? className : ''
  const errorClass = isInvalid ? 'error' : ''

  return (
    <div id={`${id}-select-container`} className='select-container'>
      {label && <InputLabel id={id} labelTranslationKey={label} propertyName={name} labelClass={labelClassName} />}
      <div className='select-input-wrapper'>
        <select
          id={id}
          data-cy={id}
          className={`select ${errorClass} ${classNames}`}
          style={newStyles}
          value={value ? JSON.stringify(value) : ''}
          onChange={onSelectChange}
          onBlur={onLostFocus}
          {...(disabled && { disabled: true })}
          {...(ariaLabel && { 'aria-label': ariaLabel })}
          {...(onFocus && { onFocus })}
        >
          {placeholder && <option value={allowEmpty ? VALID : INVALID}>{placeholder}</option>}
          {dropdownOptions.map((option, index) => (
            <option value={JSON.stringify(option.value)} key={index}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      {isInvalid && hasErrors() && (
        <div role='alert' className='font-error' aria-labelledby={id + '-input-label'}>
          <div className='caption caption--error'>
            <IconS24CrossCircle24Filled className={'caption-icon'} />
            {t(`validation.${errors[0]}`)}
          </div>
        </div>
      )}
    </div>
  )
}

export default DropdownSelect
